/**
 * Decision rules cluster — transformation × decision cell per scope §6.6.
 *
 * Formal cluster rendering precedent: PerEntityConfigurationCluster
 * (Atlas Day 2 P3). Cluster header + "Together:" line + 5 sub-region
 * cards tiled within cluster cell.
 *
 * Cluster header copy verbatim from scope §6.6 L431 (verified Atlas Day 3a
 * Phase 0 #7):
 *   "Decision rules — five named rules encoding ISG's operational gates
 *    and strategic ranking. Four rules gate which products pass; one rule
 *    ranks among products that pass. Together: ISG's encoded posture for
 *    what's worth buying."
 *
 * Operational-gates umbrella framing (per Atlas Day 3a build prompt
 * convention reminder): "four rules gate" is umbrella covering true gates
 * (Credit gate, Path eligibility), flag-and-report (MAP per ADR 41 Shape
 * B), and flagging-with-decision-effect (Restock urgency tiers). Each
 * rule inspect surfaces actual mechanism honestly.
 *
 * Substrate fetched on mount via fetchTransformationRowSubstrate
 * (transformationRowSubstrate.ts). Each card receives substrate slice as
 * props. RestockUrgencyTiersCard takes no substrate (RPC-default values
 * per Sol D-2 lock).
 */

import { useEffect, useState, type JSX } from 'react';
import {
  fetchTransformationRowSubstrate,
  type TransformationRowSubstrate,
} from '../../../lib/atlas/transformationRowSubstrate';
import MapEnforcementCard from './MapEnforcementCard';
import CreditGateCard from './CreditGateCard';
import PathEligibilityCard from './PathEligibilityCard';
import RestockUrgencyTiersCard from './RestockUrgencyTiersCard';
import BuyScoreCompositionCard from './BuyScoreCompositionCard';

export default function DecisionRulesCluster(): JSX.Element {
  const [substrate, setSubstrate] = useState<TransformationRowSubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchTransformationRowSubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="decision-rules"
      data-cell-coordinate="transformation-decision"
      data-region-type="cluster"
      className="border border-border bg-surface-1 rounded-lg p-4 space-y-3"
    >
      {/* Cluster header per scope §6.6 verbatim */}
      <div>
        <h2 className="text-sm font-semibold text-ink-bright">Decision rules</h2>
        <p className="text-xs text-ink-secondary mt-1">
          Five named rules encoding ISG&apos;s operational gates and strategic ranking. Four
          rules gate which products pass; one rule ranks among products that pass.
        </p>
        <p className="text-xs text-ink-secondary mt-1">
          <span className="font-semibold text-ink-bright">Together:</span> ISG&apos;s encoded
          posture for what&apos;s worth buying.
        </p>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      {/* 5 sub-region cards per scope §6.6.1-§6.6.5 */}
      {error ? (
        <p className="text-xs text-ink-muted italic">Decision rules substrate currently unavailable.</p>
      ) : !substrate ? (
        <div className="space-y-2" aria-label="Loading decision rules substrate">
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
        </div>
      ) : (
        <div className="space-y-2">
          <MapEnforcementCard substrate={substrate.mapEnforcement} />
          <CreditGateCard substrate={substrate.creditGate} />
          <PathEligibilityCard substrate={substrate.pathEligibility} />
          <RestockUrgencyTiersCard />
          <BuyScoreCompositionCard substrate={substrate.buyScoreComposition} />
        </div>
      )}
    </section>
  );
}
